import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/auth.service';
import { setGetAuthToken, setGetRefreshToken, setSetTokens, setGetTenantId, setGetSelectedOrgId } from '../services/api';
import { useMasterSystemStore } from './master-system.store';
import type { User } from '../types/auth.types';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string, tenantId?: string | null) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  forgotPassword: (email: string, tenantId?: string | null) => Promise<void>;
  resetPassword: (token: string, newPassword: string, tenantId?: string | null) => Promise<void>;
}

/**
 * Mensagem genérica de erro para falhas de credencial.
 *
 * SEGURANÇA: nunca revelar se o usuário existe em outro tenant ou se
 * a senha está incorreta — sempre uma única mensagem opaca.
 */
const GENERIC_AUTH_ERROR_MESSAGE = 'Credenciais inválidas';

interface AxiosLikeError {
  response?: { status?: number; data?: { error?: { message?: string }; message?: string } };
  message?: string;
}

/**
 * Extrai mensagem de erro de auth, mascarando 401 com mensagem genérica
 * para defender contra user-enumeration cross-tenant.
 */
function resolveAuthErrorMessage(error: unknown, fallback: string): string {
  const err = error as AxiosLikeError;
  if (err.response?.status === 401) {
    return GENERIC_AUTH_ERROR_MESSAGE;
  }
  return (
    err.response?.data?.error?.message ||
    err.response?.data?.message ||
    err.message ||
    fallback
  );
}

/**
 * Auth Store (Zustand)
 *
 * Manages authentication state globally.
 * Persists token and user data to localStorage.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string, tenantId?: string | null) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login({
            email,
            password,
            ...(tenantId ? { tenantId } : {}),
          });

          set({
            user: response.user,
            token: response.accessToken,
            refreshToken: response.refreshToken ?? null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          const errorMessage = resolveAuthErrorMessage(
            error,
            'Erro ao fazer login. Tente novamente.',
          );

          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
          });

          throw error;
        }
      },

      verifyMagicLink: async (token: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.verifyMagicLink(token);

          set({
            user: response.user,
            token: response.accessToken,
            refreshToken: response.refreshToken ?? null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          const errorMessage = resolveAuthErrorMessage(error, 'Link inválido ou expirado.');

          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });

          throw new Error(errorMessage);
        }
      },

      logout: () => {
        authService.logout().catch(() => {});
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
        window.location.href = '/login';
      },

      checkAuth: async () => {
        const { token, refreshToken } = get();

        if (!token) {
          set({ isAuthenticated: false, user: null, token: null, refreshToken: null });
          return;
        }

        set({ isLoading: true });

        try {
          const user = await authService.getCurrentUser();

          // Detect stale JWT role: if the DB role differs from the JWT role,
          // auto-refresh the token so the backend sees the current role.
          if (refreshToken && user.role) {
            try {
              const base64Url = token.split('.')[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const payload = JSON.parse(atob(base64));
              if (payload.role !== user.role) {
                const response = await authService.refreshToken({ refreshToken });
                set({
                  user: response.user,
                  token: response.accessToken,
                  refreshToken: response.refreshToken ?? refreshToken,
                  isAuthenticated: true,
                  isLoading: false,
                });
                return;
              }
            } catch {
              // JWT decode or refresh failed — continue with current token
            }
          }

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // Try refresh if we have a refresh token
          if (refreshToken) {
            try {
              const response = await authService.refreshToken({ refreshToken });
              set({
                user: response.user,
                token: response.accessToken,
                refreshToken: response.refreshToken ?? null,
                isAuthenticated: true,
                isLoading: false,
              });
              return;
            } catch {
              // Refresh also failed
            }
          }

          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setTokens: (accessToken: string, newRefreshToken?: string) => {
        set({
          token: accessToken,
          refreshToken: newRefreshToken ?? get().refreshToken,
        });
      },

      forgotPassword: async (email: string, tenantId?: string | null) => {
        set({ isLoading: true, error: null });
        try {
          await authService.forgotPassword({
            email,
            ...(tenantId ? { tenantId } : {}),
          });
          set({ isLoading: false });
        } catch (error: unknown) {
          const errorMessage = resolveAuthErrorMessage(
            error,
            'Erro ao enviar email de recuperação.',
          );
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      resetPassword: async (token: string, newPassword: string, tenantId?: string | null) => {
        set({ isLoading: true, error: null });
        try {
          await authService.resetPassword({
            token,
            newPassword,
            ...(tenantId ? { tenantId } : {}),
          });
          set({ isLoading: false });
        } catch (error: unknown) {
          const errorMessage = resolveAuthErrorMessage(error, 'Erro ao redefinir senha.');
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

// Register token getter functions for API interceptor
setGetAuthToken(() => useAuthStore.getState().token);
setGetRefreshToken(() => useAuthStore.getState().refreshToken);
setGetTenantId(() => useAuthStore.getState().user?.tenantId ?? null);
setGetSelectedOrgId(
  () => useMasterSystemStore.getState().selectedOrganizationId,
);
setSetTokens((accessToken, refreshToken) => {
  useAuthStore.getState().setTokens(accessToken, refreshToken);
});

// Export a getter for refreshToken (used by api.ts interceptor)
export function getRefreshToken(): string | null {
  return useAuthStore.getState().refreshToken;
}

export function setTokensFromRefresh(accessToken: string, refreshToken?: string): void {
  useAuthStore.getState().setTokens(accessToken, refreshToken);
}
